import { Icon } from "@iconify/react";
import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Button, Table, Alert } from "react-bootstrap";

import {
  getUploadedDocumentByEntityId,
  uploadFilesEntity,
} from "../../../serviceBroker/entityUploadedApiServiceBroker";
import { generateGuid, getLabelName } from "../../../utils";
import { UploadedFileModel, EntityDocumentTypeEnum } from "../../../models";
import { useGenericSystemConfigurations } from "../../../hooks";
import { ModelDialogBox, ButtonBox, LoadingBox } from "../../index";

type Props = {
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFileModel[]>>;
  uploadedFiles: UploadedFileModel[];
  entityDocumentType: keyof typeof EntityDocumentTypeEnum;
  requestId: number;
};

export const UploadDialog = React.forwardRef(
  (
    { setUploadedFiles, uploadedFiles, entityDocumentType, requestId }: Props,
    ref: React.ForwardedRef<{ resetFiles: () => void }>
  ) => {
    //#region state
    const [showModel, setShowModel] = useState(false);
    const [files, setFiles] = useState<any[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [description, setDescription] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const { genericSystemConfigurations } =
      useGenericSystemConfigurations(setIsLoading);
    const dataLoaded = useRef(false);
    //#endregion
    //#region functions
    const handleChangeRequest = async () => {
      setIsLoading(true);
      const documents = await getUploadedDocumentByEntityId({
        entityDocumentTypeId: EntityDocumentTypeEnum[entityDocumentType],
        entityId: requestId,
      });
      setIsLoading(false);
      setUploadedFiles(documents.Result ?? []);
    };
    const resetFiles = () => {
      setFiles([]);
      setSelectedFile(null);
      setDescription("");
      setFileError(null);
      setUploadedFiles([]);
    };
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setFileError(null);

      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        if (
          !Array.isArray(acceptedFileTypes) ||
          !acceptedFileTypes.includes(file.type)
        ) {
          setFileError(getLabelName(`this file not allowed`));
          setSelectedFile(null);
          return;
        }

        if (
          !maxFileSize || // covers null, undefined, 0, or NaN
          isNaN(maxFileSize) ||
          file.size > maxFileSize
        ) {
          setFileError(
            getLabelName(
              `File size should not exceed ${maxFileSize / 1024} bytes`
            )
          );
          setSelectedFile(null);
          return;
        }

        convertToBase64(file);
      }
    };
    const convertToBase64 = (file: File) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result) {
          setSelectedFile(file);
        }
      };
    };
    const handleAdd = () => {
      if (!description) {
        setFileError(getLabelName("Description Is Required"));
        return;
      }
      if (selectedFile) {
        const gud = generateGuid();
        const reader = new FileReader();
        reader.readAsDataURL(selectedFile);
        reader.onload = () => {
          if (reader.result) {
            const base64Data = (reader.result as string).split(",")[1];
            setFiles([
              ...files,
              {
                Name: selectedFile.name,
                FileDescription: description,
                File64Bit: base64Data,
                FileGuid: gud,
                rowState: 1,
              },
            ]);
            setSelectedFile(null);
            setFileError(null);
            setDescription("");
          }
        };
      }
    };
    const handleDelete = (file: any) => {
      if (file?.ID) {
        setUploadedFiles((prevFiles) => {
          const index = prevFiles.findIndex(
            (x) => x.FileGuid === file.FileGuid
          );
          prevFiles[index].rowState = 3;
          return [...prevFiles];
        });
      } else if (file?.rowState || file?.rowState === 0) {
        setUploadedFiles((prevFiles) => {
          return prevFiles?.filter((f, i) => file.FileGuid !== f.FileGuid);
        });
      } else {
        setFiles((prevFiles) => {
          return prevFiles?.filter((f, i) => file.FileGuid !== f.FileGuid);
        });
      }
    };
    const handleDownload = (file: any) => {
      const link = document.createElement("a");
      // Ensure the Base64 string is a valid data URL
      link.href = `data:application/${file.Name.split(".").pop()};base64,${
        file.File64Bit
      }`;
      link.download = file.Name;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    const handleSubmit = async () => {
      setIsLoading(true);
      const filesToSubmit = [...files, ...uploadedFiles.filter((x) => !x.ID)];
      if (filesToSubmit.length === 0) {
        setIsLoading(false);
        setShowModel(false);
        return;
      }

      try {
        const response = await uploadFilesEntity(filesToSubmit);

        if (response.IsSuccess && response.Result) {
          const updatedFiles = [
            ...(response.Result ?? []),
            ...uploadedFiles.filter((x) => x.ID),
          ];

          setFiles([]);
          setUploadedFiles(updatedFiles);
          setDescription("");
          setSelectedFile(null);
          setIsLoading(false);
          setShowModel(false);
        } else {
          setFileError(getLabelName("Failed to upload files"));
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error uploading files:", error);
        setFileError(getLabelName("An error occurred while uploading files"));
      }
    };
    //#endregion
    //#region useEffect
    useEffect(() => {
      const fillData = async () => {
        if (showModel && !dataLoaded.current) {
          await handleChangeRequest();
          dataLoaded.current = true;
        }
      };
      fillData().then(() => {});
    }, [showModel, entityDocumentType, requestId, handleChangeRequest]);

    useEffect(() => {
      dataLoaded.current = false;
    }, [entityDocumentType, requestId]);
    //#endregion
    //#region variables
    // Maximum file size: KB
    const maxFileSize =
      genericSystemConfigurations?.MaxFileToUploadInKB &&
      !isNaN(genericSystemConfigurations.MaxFileToUploadInKB)
        ? genericSystemConfigurations.MaxFileToUploadInKB * 1024
        : 0;
    // Accepted file types
    const acceptedFileTypes = Array.isArray(
      genericSystemConfigurations?.FilesTypeAllowedToUpload
    )
      ? (genericSystemConfigurations.FilesTypeAllowedToUpload as string[])
      : ([] as string[]);
    //#endregion
    //#region constants
    // Make this method accessible via ref
    React.useImperativeHandle(ref, () => ({
      resetFiles,
    }));
    //#endregion
    //#region html
    return (
      <Col xxl={4} xl={4} lg={4} md={4} sm={12}>
        {genericSystemConfigurations?.IsEnableToUploadAndAttachDocumentInTransaction ? (
          <div className="d-flex align-items-center gap-3">
            <p className="m-0">{getLabelName("upload files")}</p>
            <Icon
              onClick={() => setShowModel(true)}
              icon="ep:upload-filled"
              fontSize={40}
              className="cursor-pointer"
            />
          </div>
        ) : null}

        <ModelDialogBox
          isModelVisible={showModel}
          size="xl"
          title={getLabelName("upload files")}
          children={
            <div className="w-100 d-flex flex-column gap-3 p-4 bg-white shadow-sm rounded">
              <div className="d-flex align-items-center gap-2">
                <Form.Label className="cursor-pointer d-flex align-items-center gap-2 border p-2 rounded">
                  <Icon icon="ep:upload-filled" fontSize={40} />
                  <span>{getLabelName("Attach File")}</span>
                  <Form.Control
                    style={{ display: "none" }}
                    type="file"
                    onChange={handleFileChange}
                    accept={acceptedFileTypes?.join(",")}
                  />
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder={getLabelName("description")}
                  style={{ flex: 1 }}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <Button
                  variant="primary"
                  onClick={handleAdd}
                  disabled={!selectedFile || !description.trim()}
                >
                  {getLabelName("add")}
                </Button>
              </div>
              {selectedFile?.name && <div>{selectedFile?.name}</div>}

              {fileError && <Alert variant="danger">{fileError}</Alert>}

              <div className="my-2">
                <small className="text-muted">
                  {getLabelName(
                    `Allowed files: images and PDF. Maximum size:${
                      maxFileSize / 1024
                    } KB`
                  )}
                </small>
              </div>

              {(uploadedFiles?.filter((file) => file?.rowState !== 3).length >
                0 ||
                files.length > 0) && (
                <Table bordered>
                  <thead className="bg-light text-center">
                    <tr>
                      <th>{getLabelName("file name")}</th>
                      <th>{getLabelName("description")}</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedFiles
                      ?.filter((file) => file?.rowState !== 3)
                      .concat(files)
                      .map((file, index) => (
                        <tr key={index} className="text-center">
                          <td>{file.Name}</td>
                          <td>{file.FileDescription}</td>
                          <td style={{ color: "red" }}>
                            <Icon
                              style={{ cursor: "pointer" }}
                              onClick={() => handleDelete(file)}
                              icon="iwwa:delete"
                              fontSize={20}
                            />
                          </td>
                          <td>
                            <Icon
                              style={{ cursor: "pointer" }}
                              onClick={() => handleDownload(file)}
                              icon="solar:download-outline"
                              fontSize={20}
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              )}

              <div className="d-flex gap-3">
                <ButtonBox
                  disabled={isLoading}
                  variant="success"
                  size="sm"
                  onClick={handleSubmit}
                >
                  {isLoading ? (
                    <LoadingBox isLoading={isLoading} />
                  ) : (
                    getLabelName("save")
                  )}
                </ButtonBox>
                <ButtonBox
                  variant="danger"
                  size="sm"
                  iconType="close-circle"
                  onClick={() => {
                    setFiles([]);
                    setShowModel(false);
                    setIsLoading(false);
                  }}
                >
                  {getLabelName("close")}
                </ButtonBox>
              </div>
            </div>
          }
        />
      </Col>
    );
    //#endregion
  }
); //export default UploadDialog;

/**
 * <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-4 col-sm-12 flex items-center">
            <Icon onClick={()=>setShowModel(true)} className='tw-cursor-pointer' icon="ep:upload-filled" fontSize={40}/>
            <ModelDialogBox 
                isModelVisible={showModel} 
                size='xl'
                title='upload files'
                children={
                    <div className="tw-w-full tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-bg-white tw-shadow-md tw-rounded-lg">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <label className="tw-cursor-pointer tw-flex tw-items-center tw-gap-2 tw-border tw-p-2 tw-rounded-lg">
                                <Icon icon="ep:upload-filled" fontSize={40}/>
                                <span>{selectedFile ? selectedFile.name : "Upload File"}</span>
                                <input type="file" className="tw-hidden" onChange={handleFileChange} />
                            </label>
                            <input
                                type="text"
                                placeholder="Description"
                                className="tw-border tw-p-2 tw-rounded-md tw-flex-1"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                            <button
                                onClick={handleAdd}
                                className="tw-bg-blue-500 tw-text-white tw-px-4 tw-py-2 tw-rounded-lg"
                                disabled={!selectedFile || !description.trim()}
                            >
                              Add
                            </button>
                        </div>

                        {files.length > 0 && (
                            <table className="tw-w-full tw-border-collapse tw-border">
                                <thead>
                                    <tr className="tw-bg-gray-200 tw-text-center">
                                    <th className="tw-border tw-p-2">File Name</th>
                                    <th className="tw-border tw-p-2">Description</th>
                                    <th className="tw-border tw-p-2">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {files.map((file, index) => (
                                    <tr key={index} className="tw-border tw-text-center">
                                        <td className="tw-border tw-p-2">{file.name}</td>
                                        <td className="tw-border tw-p-2">{file.FileDescription}</td>
                                        <td className="tw-border tw-p-2">
                                        <button
                                            onClick={() => handleDelete(index)}
                                            className="tw-text-red-500 tw-hover:text-red-700"
                                        >
                                            <Icon icon="iwwa:delete" fontSize={20}/>
                                        </button>
                                        </td>
                                    </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        <div className='tw-flex tw-gap-4'>

                           <ButtonBox
                                variant="success"
                                size="sm"
                                onClick={() => handleSubmit()}
                            >
                                save
                            </ButtonBox>
                            <ButtonBox
                                variant="danger"
                                size="sm"
                                iconType="close-circle"
                                onClick={() => setShowModel(false)}
                            >
                                close
                            </ButtonBox>

                        </div>
                    </div>
                }
            />
       </div>
 */
